// ============================================================
// ELC Shift — Uploads / Compliance module (v104.0)
//
// Compliance scan + reporting: daily completion tracking,
// email summaries to area managers, scan config (which logs
// count toward compliance score).
//
// Public surface:
//   • loadComplianceScanConfig
//   • loadComplianceSummary
//   • renderComplianceStats
//   • renderComplianceTable
//   • formatComplianceEmail
//   • toggleComplianceSettings
//   • renderComplianceSettingsLists
//   • saveComplianceSettings
//
// Free globals consumed:
//   DB, showToast, escapeHtml, formatDateShort, ALL_STORES
// ============================================================

import { adminToken, SIGNIN_EXPIRED_MESSAGE, isSignInExpired } from '../shared/admin-token.js';   // v136.12

export async function loadComplianceScanConfig() {
  try {
    const row = await DB.config.get('compliance_scan_config');
    if (row && row.value && typeof row.value === 'object') {
      _complianceScanCfg = row.value;
    } else {
      _complianceScanCfg = null;
    }
  } catch (e) {
    _complianceScanCfg = null;
  }
}

export async function loadComplianceSummary() {
  const range = complianceRangeFor(_compliancePeriod);
  const label = document.getElementById('compliancePeriodLabel');
  if (label) label.textContent = range.from + ' → ' + range.to;

  const body = document.getElementById('complianceBody');
  if (body) body.innerHTML = '<tr><td colspan="4" style="padding:30px;text-align:center;color:var(--sub);font-size:12px">Scanning…</td></tr>';

  try {
    // cache:'no-store' is load-bearing, not cosmetic. The SW skips the
    // workers.dev origin, so this admin GET otherwise falls to the BROWSER
    // HTTP cache: a manager who opened the report before the store finished
    // its logs would keep seeing the stale "X missing" snapshot for the same
    // period even after the logs landed (same URL = cache hit). Marina Mall
    // hit exactly this. Same fix as the Reviews tab (v124.6). Don't remove.
    const res = await fetch(DB.workerUrl() + '/admin/compliance-summary?from=' + range.from + '&to=' + range.to, {
      cache: 'no-store',
      // v136.12: the shared token read (DB's token, then this tab, then the remembered copy). It read
      // sessionStorage alone when an area manager saw "Scan failed: HTTP 401" on staging (19 Oct 2026).
      headers: { 'Authorization': 'Bearer ' + adminToken() },
    });
    // v136.12: a 401 means the sign-in has ended; say so in plain words, not the raw HTTP text.
    if (isSignInExpired(res.status)) { const ex = new Error(SIGNIN_EXPIRED_MESSAGE); ex.signInExpired = true; throw ex; }
    if (!res.ok) throw new Error('HTTP ' + res.status + ': ' + (await res.text()));
    _complianceData = await res.json();
    renderComplianceStats();
    renderComplianceTable();
  } catch (e) {
    console.warn('[compliance] load failed:', e && e.message);
    if (body && e && e.signInExpired) body.innerHTML = '<tr><td colspan="4" style="padding:30px;text-align:center;color:var(--red);font-size:12px">' + escapeHtml(SIGNIN_EXPIRED_MESSAGE) + '</td></tr>';
    else if (body) body.innerHTML = '<tr><td colspan="4" style="padding:30px;text-align:center;color:var(--red);font-size:12px">Scan failed: ' + escapeHtml(e.message || String(e)) + '</td></tr>';
    _complianceData = null;
  }
}

export function renderComplianceStats() {
  const el = document.getElementById('complianceStats');
  if (!el || !_complianceData) return;
  const s = _complianceData.stats || { total: 0, by_category: {}, by_area_manager: {} };
  if (s.total === 0) {
    el.innerHTML = '<div style="padding:12px 16px;background:var(--ok-bg);border:1px solid var(--ok-border);border-radius:10px;color:var(--ok);font-size:13px;font-weight:600">✓ No issues found for this period</div>';
    return;
  }
  const cats = Object.keys(s.by_category).sort();
  // Total badge + per-category counts.
  el.innerHTML = '<div style="padding:10px 14px;background:var(--warn-bg);border:1px solid var(--adm-warn-line);border-radius:10px;color:var(--warn-deep);font-size:13px;font-weight:700">'
    + s.total + ' issue' + (s.total === 1 ? '' : 's') + '</div>'
    + cats.map(c => '<div style="padding:8px 12px;background:var(--card);border:1px solid var(--border);border-radius:10px;font-size:12px"><strong>' + s.by_category[c] + '</strong> <span style="color:var(--sub)">' + categoryLabel(c) + '</span></div>').join('');
}

export function renderComplianceTable() {
  const body = document.getElementById('complianceBody');
  if (!body || !_complianceData) return;

  const fMgr = document.getElementById('complianceFilterManager').value;
  const fCat = document.getElementById('complianceFilterCategory').value;
  const filtered = (_complianceData.issues || []).filter(i => {
    if (fMgr && i.area_manager !== fMgr) return false;
    if (fCat && i.category    !== fCat) return false;
    return true;
  });

  if (filtered.length === 0) {
    body.innerHTML = '<tr><td colspan="4" style="padding:30px;text-align:center;color:var(--sub);font-size:12px">' + (_complianceData.stats && _complianceData.stats.total === 0 ? 'No issues found.' : 'No issues match the filters.') + '</td></tr>';
    return;
  }

  // Sort: area manager, then store, then category (so the table mirrors
  // the email export grouping).
  filtered.sort((a, b) => {
    if (a.area_manager !== b.area_manager) return (a.area_manager || '').localeCompare(b.area_manager || '');
    if (a.store !== b.store)               return (a.store || '').localeCompare(b.store || '');
    return (a.category || '').localeCompare(b.category || '');
  });

  body.innerHTML = filtered.map(i => {
    const catBadge = '<span style="font-size:11px;padding:2px 7px;border-radius:10px;background:var(--surface-3);color:var(--sub);display:inline-block">' + categoryLabel(i.category) + (i.subtype_label ? ' <span translate="no">· ' + escapeHtml(i.subtype_label) + '</span>' : '') + '</span>';
    return '<tr>'
      + '<td style="font-size:12px" translate="no">' + escapeHtml(i.area_manager || '—') + '</td>'
      + '<td style="font-size:12px"><strong translate="no">' + escapeHtml(i.store || '—') + '</strong></td>'
      + '<td>' + catBadge + '</td>'
      + '<td style="font-size:12px;color:var(--text)">' + escapeHtml(i.detail || '')
          + (i.missing_dates && i.missing_dates.length ? ' <span style="color:var(--sub)">(' + escapeHtml(fmtMissedDates(i.missing_dates)) + ')</span>' : '')
        + '</td>'
      + '</tr>';
  }).join('');
}

// Format the backend's missing_dates (['YYYY-MM-DD', …]) into a short readable string like
// "Tue 7 Jul", so "Missing 1 of 7" reads "Missing 1 of 7 (Tue 7 Jul)" — self-diagnosing, and
// instantly tells an AM whether it's a real miss or a business-day edge. UTC math avoids any
// local-timezone day shift. (Function declaration → hoisted; used by the table above + email.)
export function fmtMissedDates(dates) {
  if (!Array.isArray(dates) || !dates.length) return '';
  const dys = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  const mns = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  const one = function (d) {
    const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(d || ''));
    if (!m) return String(d);
    const dt = new Date(Date.UTC(+m[1], +m[2] - 1, +m[3]));
    if (isNaN(dt.getTime())) return String(d);
    return dys[dt.getUTCDay()] + ' ' + (+m[3]) + ' ' + mns[+m[2] - 1];
  };
  const out = dates.map(one);
  return out.length > 4 ? out.slice(0, 4).join(', ') + ' +' + (out.length - 4) + ' more' : out.join(', ');
}

export function formatComplianceEmail(data) {
  if (!data || !data.issues || data.issues.length === 0) return 'No issues for this period.';

  const byMgr = {};
  data.issues.forEach(i => {
    const mgr = i.area_manager || 'Unassigned';
    if (!byMgr[mgr]) byMgr[mgr] = [];
    byMgr[mgr].push(i);
  });

  const out = [];
  out.push('Compliance summary — ' + data.period.from + ' to ' + data.period.to + ' (' + data.period.days + ' day' + (data.period.days === 1 ? '' : 's') + ')\n');

  Object.keys(byMgr).sort().forEach(mgr => {
    out.push('Issues for @' + mgr + ':');
    const items = byMgr[mgr];

    // Group by (category, subtype) so we can roll up "stores not doing X"
    const groups = {};
    items.forEach(i => {
      const key = i.category + (i.subtype ? ':' + i.subtype : '');
      if (!groups[key]) groups[key] = { category: i.category, subtype: i.subtype, subtype_label: i.subtype_label, items: [] };
      groups[key].items.push(i);
    });

    Object.values(groups).forEach(g => {
      if (g.category === 'log_compliance' || g.category === 'checklist_compliance') {
        out.push('');
        out.push('Stores not doing ' + (g.subtype_label || g.subtype) + ':');
        g.items.forEach(i => out.push('  - ' + i.store + ' (' + i.detail + (i.missing_dates && i.missing_dates.length ? ' — ' + fmtMissedDates(i.missing_dates) : '') + ')'));
      } else if (g.category === 'late_clockin') {
        out.push('');
        out.push('Late clock-ins:');
        g.items.forEach(i => out.push('  - ' + i.store + ': ' + i.detail));
      } else if (g.category === 'break_issue') {
        out.push('');
        out.push('Break issues:');
        g.items.forEach(i => out.push('  - ' + i.store + ': ' + i.detail));
      } else if (g.category === 'location_off') {
        out.push('');
        out.push('Location services not on:');
        g.items.forEach(i => out.push('  - ' + i.store + ': ' + i.detail));
      } else if (g.category === 'never_signed_in') {
        out.push('');
        out.push('Staff never signed in:');
        g.items.forEach(i => out.push('  - ' + i.store + ': ' + i.detail));
      }
    });
    out.push('');
    out.push('---');
    out.push('');
  });

  return out.join('\n');
}

export async function toggleComplianceSettings() {
  const panel = document.getElementById('complianceSettingsPanel');
  if (!panel) return;
  const showing = panel.style.display !== 'none';
  if (showing) {
    panel.style.display = 'none';
    return;
  }
  // Opening: populate the checkboxes from current templates + saved config.
  panel.style.display = 'block';
  await renderComplianceSettingsLists();
}

export async function renderComplianceSettingsLists() {
  const logList = document.getElementById('complianceLogTypeList');
  const chkList = document.getElementById('complianceChecklistTypeList');
  if (!logList || !chkList) return;

  // Fetch templates + the latest persisted config in parallel. Templates
  // come from the same admin endpoints the Logs / Checklists tabs use, so
  // they're already cached after a session of normal admin work.
  let logTemplates = [];
  let checklistTemplates = [];
  try {
    const adminToken = localStorage.getItem('elc_admin_token') || '';
    const headers = { 'Authorization': 'Bearer ' + adminToken };
    const [logRes, chkRes] = await Promise.all([
      // no-store for the same reason as the summary fetch above — a stale
      // cached template list would show the wrong scan-settings checkboxes.
      fetch(DB.workerUrl() + '/admin/log-templates',       { headers, cache: 'no-store' }).then(r => r.ok ? r.json() : []),
      fetch(DB.workerUrl() + '/admin/checklist-templates', { headers, cache: 'no-store' }).then(r => r.ok ? r.json() : []),
    ]);
    logTemplates       = (logRes || []).filter(t => t.active !== false);
    checklistTemplates = (chkRes || []).filter(t => t.active !== false);
  } catch (e) {
    console.warn('[compliance settings] template fetch failed:', e && e.message);
  }

  // Re-load the persisted config in case another admin saved it since init.
  await loadComplianceScanConfig();

  // Default: all ticked if no config. If config present, tick only listed.
  const logAllowed = (_complianceScanCfg && Array.isArray(_complianceScanCfg.log_types))
    ? new Set(_complianceScanCfg.log_types)
    : null;
  const chkAllowed = (_complianceScanCfg && Array.isArray(_complianceScanCfg.checklist_types))
    ? new Set(_complianceScanCfg.checklist_types)
    : null;

  if (logTemplates.length === 0) {
    logList.innerHTML = '<div style="color:var(--sub);font-size:12px;padding:6px 0">No log templates configured.</div>';
  } else {
    logList.innerHTML = logTemplates.map(t => {
      const checked = (logAllowed === null) ? true : logAllowed.has(t.type);
      const freq = t.frequency ? ' <span style="color:var(--sub);font-size:11px">· ' + escapeHtml(t.frequency) + '</span>' : '';
      return '<label style="display:flex;align-items:center;gap:8px;padding:5px 0;cursor:pointer">'
        + '<input type="checkbox" data-kind="log" data-type="' + escapeAttr(t.type) + '"' + (checked ? ' checked' : '') + ' style="cursor:pointer">'
        + '<span translate="no">' + (t.icon ? escapeHtml(t.icon) + ' ' : '') + escapeHtml(t.name || t.type) + freq + '</span>'
        + '</label>';
    }).join('');
  }

  if (checklistTemplates.length === 0) {
    chkList.innerHTML = '<div style="color:var(--sub);font-size:12px;padding:6px 0">No checklists configured.</div>';
  } else {
    chkList.innerHTML = checklistTemplates.map(t => {
      const checked = (chkAllowed === null) ? true : chkAllowed.has(t.type);
      return '<label style="display:flex;align-items:center;gap:8px;padding:5px 0;cursor:pointer">'
        + '<input type="checkbox" data-kind="checklist" data-type="' + escapeAttr(t.type) + '"' + (checked ? ' checked' : '') + ' style="cursor:pointer">'
        + '<span translate="no">' + escapeHtml(t.name || t.type) + '</span>'
        + '</label>';
    }).join('');
  }
}

export async function saveComplianceSettings() {
  const panel = document.getElementById('complianceSettingsPanel');
  if (!panel) return;
  const logTypes = [];
  const chkTypes = [];
  panel.querySelectorAll('input[type="checkbox"][data-kind]:checked').forEach(cb => {
    const kind = cb.getAttribute('data-kind');
    const type = cb.getAttribute('data-type');
    if (kind === 'log')       logTypes.push(type);
    if (kind === 'checklist') chkTypes.push(type);
  });
  const cfg = { log_types: logTypes, checklist_types: chkTypes };
  try {
    await DB.config.set('compliance_scan_config', cfg);
    _complianceScanCfg = cfg;
    showToast('Scan settings saved', 'success');
    panel.style.display = 'none';
    // Re-run the scan so the table immediately reflects the new scope.
    loadComplianceSummary();
  } catch (e) {
    showToast('Save failed: ' + (e && e.message), 'error');
  }
}

